/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useRef, useState } from 'react';
import './FormDatePicker.scss';
import PropTypes from 'prop-types';
import { appAlert, appIconsPath, appKeyCode } from '@utils/globalConstant';
import { Notification } from '@core/Notification';
import { format, getDaysInMonth } from 'date-fns';
import { de } from 'date-fns/locale';
import { ButtonPrimary } from '@core/ButtonPrimary';
import moment from 'moment';
import { tA11y } from '@utils/a11y/a11yHelpers';
import { sanitizeRichText } from '@utils/sanitizeHtml';
import Icons from '@core/Utils/Icons/Icons';

export function FormDatePicker({
  id = 'id',
  label = '',
  hint = '',
  value: valueProp = '',
  invalid,
  invalidMessage = '',
  valid,
  validMessage = '',
  placeholder = '',
  disabled,
  isNextYears,
  popUpPicker,
  transparentDropDown,
  customClass = '',
  onBlur = () => {},
  onChange = () => {},
  isOptional = false,
  ...restProps
}) {
  const [focusDIndex, setFocusDIndex] = useState(0);
  const [focusMIndex, setFocusMIndex] = useState(0);
  const [focusYIndex, setFocusYIndex] = useState(0);
  const [isFocusOn, setIsFocusOn] = useState(null); // D: Day, M: Month, Y: Year

  // Generate arrays for days, months, and years
  const dropMenu = useRef(null);
  const dateRefs = useRef([]);
  const monthRefs = useRef([]);
  const yearRefs = useRef([]);
  const confirmButtonRef = useRef(null);
  const datePickerRef = useRef(null);

  const [days, setDays] = useState(Array.from({ length: 31 }, (_, i) => i + 1));
  const [value, setValue] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isFocus, setIsFocus] = useState(false);
  const [isBlur, setIsBlur] = useState(false);
  const [isTouched, setIsTouched] = useState(false);

  const months = Array.from({ length: 12 }, (_, i) =>
    format(new Date(0, i), 'LLLL', { locale: de })
  );
  const prevYears = Array.from({ length: 100 }, (_, i) => new Date().getFullYear() - i);
  const nextYears = Array.from({ length: 100 }, (_, i) => new Date().getFullYear() + i);

  // State for the selected day, month, and year
  const [selectedDay, setSelectedDay] = useState(new Date().getDate());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Functions
  const toggleOpen = () => {
    if (!disabled) {
      if (isOpen) {
        setIsTouched(true);
      } else {
        setIsFocusOn(null);
      }
      setIsOpen(!isOpen);
      setIsFocus(!isFocus);
      setIsBlur(!isBlur);
    }
  };

  // Function to scroll smoothly
  const scrollSmoothly = (ref, nextIndex) => {
    ref.current[nextIndex]?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  };

  // Handlers for selection
  const handleDayChange = (day) => {
    setSelectedDay(day);
  };

  // Update the days array based on the selected month and year
  const updateDays = (monthIndex, year) => {
    const newDaysInMonth = getDaysInMonth(new Date(year, monthIndex));
    setDays(Array.from({ length: newDaysInMonth }, (_, i) => i + 1));

    const isFocusAtLastIndex = focusDIndex >= newDaysInMonth;
    if (isFocusAtLastIndex) {
      const dNextIndex = newDaysInMonth - 1;
      setFocusDIndex(dNextIndex);
      handleDayChange(days[dNextIndex]);
      scrollSmoothly(dateRefs, dNextIndex);
      setIsFocusOn('D');
    }
  };

  const handleMonthChange = (month) => {
    setSelectedMonth(month);
    // setSelectedDay(null); // Reset day selection
    if (month && selectedYear) {
      updateDays(month, selectedYear);
    }
  };
  const handleYearChange = (year) => {
    setSelectedYear(year);
    // setSelectedDay(null); // Reset day selection
    if (selectedMonth && year) {
      updateDays(selectedMonth, year);
    }
  };

  const placeholderText = () => {
    if (value !== '' && value !== null && value !== undefined) {
      return moment(value, 'DD.MM.YYYY').format('DD.MM.YYYY');
    }
    return tA11y(placeholder).isHtml ? (
      <span dangerouslySetInnerHTML={{ __html: sanitizeRichText(tA11y(placeholder).content) }} />
    ) : (
      <span>{tA11y(placeholder).content}</span>
    );
  };

  const onSelectDate = (event) => {
    const { keyCode } = event;
    event.preventDefault();
    if (keyCode === appKeyCode.ENTER || keyCode === appKeyCode.SPACE || event.type === 'click') {
      const date = new Date(selectedYear, selectedMonth, selectedDay);
      setValue(date);
      setIsOpen(false);
      setIsFocus(false);
      setIsTouched(true);
      setIsBlur(true);
      onChange(moment(date).format('DD.MM.YYYY'));
      datePickerRef.current?.focus();
    } else if (
      (event.shiftKey && keyCode === appKeyCode.TAB) ||
      keyCode === appKeyCode.ARROW_LEFT
    ) {
      datePickerRef.current?.focus();
      setIsFocusOn('Y');
    } else if (keyCode === appKeyCode.TAB || keyCode === appKeyCode.ARROW_RIGHT) {
      datePickerRef.current?.focus();
      setIsFocusOn('D');
    } else if (keyCode === appKeyCode.ESCAPE) {
      setIsOpen(false);
      setIsFocus(false);
      setIsBlur(true);
    }
  };

  const closeDropdown = () => {
    setIsOpen(false);
    setIsFocusOn(null);
  };

  let keyPressTimeout = null; // Global variable to store timeout

  const handleKeyDown = (event) => {
    const { keyCode } = event;
    // Prevent page scrolling, page bouncing and stop event propagation
    if (!isOpen && keyCode === appKeyCode.SPACE) {
      event.preventDefault();
    }
    if (
      isOpen &&
      [
        appKeyCode.ESCAPE,
        appKeyCode.SPACE,
        appKeyCode.ENTER,
        appKeyCode.ARROW_UP,
        appKeyCode.ARROW_DOWN,
        appKeyCode.ARROW_LEFT,
        appKeyCode.ARROW_RIGHT,
        appKeyCode.HOME,
        appKeyCode.END,
        appKeyCode.TAB,
        appKeyCode.PAGE_UP,
        appKeyCode.PAGE_DOWN
      ].includes(keyCode)
    ) {
      event.preventDefault();
    }

    // Throttle repeated key events
    if (keyPressTimeout) {
      clearTimeout(keyPressTimeout);
    }

    const yearsArr = isNextYears ? nextYears : prevYears;
    keyPressTimeout = setTimeout(() => {
      switch (keyCode) {
        case appKeyCode.ESCAPE:
          closeDropdown();
          break;

        case appKeyCode.ENTER:
        case appKeyCode.SPACE: {
          if (!isOpen) {
            setIsOpen(true);
          } else if (isOpen) {
            if (isFocusOn === 'D') {
              handleDayChange(days[focusDIndex]);
            } else if (isFocusOn === 'M') {
              handleMonthChange(focusMIndex);
            } else if (isFocusOn === 'Y') {
              handleYearChange(yearsArr[focusYIndex]);
            }
          }
          break;
        }

        case appKeyCode.HOME:
        case appKeyCode.END: {
          if (isOpen) {
            let dNextIndex;
            if (keyCode === appKeyCode.HOME) {
              dNextIndex = 0;
            } else if (keyCode === appKeyCode.END) {
              dNextIndex = days.length - 1;
            }
            setFocusDIndex(dNextIndex);
            handleDayChange(days[dNextIndex]);
            scrollSmoothly(dateRefs, dNextIndex);
          }
          break;
        }

        case appKeyCode.PAGE_UP:
        case appKeyCode.PAGE_DOWN: {
          if (isOpen) {
            if (event.shiftKey) {
              let yNextIndex;
              if (keyCode === appKeyCode.PAGE_UP) {
                if (focusYIndex === 0) {
                  yNextIndex = yearsArr.length - 1;
                } else {
                  yNextIndex = focusYIndex - 1;
                }
              } else if (keyCode === appKeyCode.PAGE_DOWN) {
                if (focusYIndex === yearsArr.length - 1) {
                  yNextIndex = 0;
                } else {
                  yNextIndex = focusYIndex + 1;
                }
              }
              setFocusYIndex(yNextIndex);
              handleYearChange(yearsArr[yNextIndex]);
              scrollSmoothly(yearRefs, yNextIndex);
            } else if (!event.shiftKey) {
              let mNextIndex;
              if (keyCode === appKeyCode.PAGE_UP) {
                if (focusMIndex === 0) {
                  mNextIndex = months.length - 1;
                } else {
                  mNextIndex = focusMIndex - 1;
                }
              } else if (keyCode === appKeyCode.PAGE_DOWN) {
                if (focusMIndex === months.length - 1) {
                  mNextIndex = 0;
                } else {
                  mNextIndex = focusMIndex + 1;
                }
              }
              setFocusMIndex(mNextIndex);
              handleMonthChange(mNextIndex);
              scrollSmoothly(monthRefs, mNextIndex);
            }
          }
          break;
        }

        case appKeyCode.ARROW_DOWN:
        case appKeyCode.ARROW_UP: {
          if (isOpen) {
            if (keyCode === appKeyCode.ARROW_DOWN) {
              if (isFocusOn === 'D') {
                setFocusDIndex((prev) => {
                  const nextIndex = Math.min(prev + 1, days.length - 1);
                  handleDayChange(days[nextIndex]);
                  scrollSmoothly(dateRefs, nextIndex);
                  return nextIndex;
                });
              } else if (isFocusOn === 'M') {
                setFocusMIndex((prev) => {
                  const nextIndex = Math.min(prev + 1, months.length - 1);
                  handleMonthChange(nextIndex);
                  scrollSmoothly(monthRefs, nextIndex);
                  return nextIndex;
                });
              } else if (isFocusOn === 'Y') {
                setFocusYIndex((prev) => {
                  const nextIndex = Math.min(prev + 1, yearsArr.length - 1);
                  handleYearChange(yearsArr[nextIndex]);
                  scrollSmoothly(yearRefs, nextIndex);
                  return nextIndex;
                });
              } else {
                setIsFocusOn('D');
              }
            } else if (keyCode === appKeyCode.ARROW_UP) {
              if (isFocusOn === 'D') {
                setFocusDIndex((prev) => {
                  const nextIndex = Math.max(prev - 1, 0);
                  handleDayChange(days[nextIndex]);
                  scrollSmoothly(dateRefs, nextIndex);
                  return nextIndex;
                });
              } else if (isFocusOn === 'M') {
                setFocusMIndex((prev) => {
                  const nextIndex = Math.max(prev - 1, 0);
                  handleMonthChange(nextIndex);
                  scrollSmoothly(monthRefs, nextIndex);
                  return nextIndex;
                });
              } else if (isFocusOn === 'Y') {
                setFocusYIndex((prev) => {
                  const nextIndex = Math.max(prev - 1, 0);
                  handleYearChange(yearsArr[nextIndex]);
                  scrollSmoothly(yearRefs, nextIndex);
                  return nextIndex;
                });
              } else {
                setIsFocusOn('D');
              }
            }
          }
          break;
        }

        case appKeyCode.ARROW_LEFT:
        case appKeyCode.ARROW_RIGHT: {
          if (isOpen) {
            if (keyCode === appKeyCode.ARROW_RIGHT) {
              if (!isFocusOn) {
                setIsFocusOn('D');
              } else if (isFocusOn === 'D') {
                setIsFocusOn('M');
              } else if (isFocusOn === 'M') {
                setIsFocusOn('Y');
              } else if (isFocusOn === 'Y') {
                setIsFocusOn(null);
                confirmButtonRef.current?.focus();
              }
            } else if (appKeyCode.ARROW_LEFT) {
              if (isFocusOn === 'Y') {
                setIsFocusOn('M');
              } else if (isFocusOn === 'M') {
                setIsFocusOn('D');
              }
            }
          }
          break;
        }

        case appKeyCode.TAB:
          if (isOpen) {
            if (event.shiftKey) {
              // Handle Shift + Tab (Move Backward)
              if (document.activeElement === confirmButtonRef.current) {
                setIsFocusOn('Y'); // Move focus back to Year when Shift+Tab is pressed from button
              } else if (isFocusOn === 'Y') {
                setIsFocusOn('M');
              } else if (isFocusOn === 'M') {
                setIsFocusOn('D');
              } else if (isFocusOn === 'D') {
                setIsFocusOn(null); // Move focus out of date picker
                closeDropdown();
              }
              break;
            }
            // Handle Normal Tab (Move Forward)
            if (isFocusOn === null) {
              setIsFocusOn('D'); // Set initial focus to Day
            } else if (isFocusOn === 'D') {
              setIsFocusOn('M');
            } else if (isFocusOn === 'M') {
              setIsFocusOn('Y');
            } else if (isFocusOn === 'Y') {
              setIsFocusOn(null);
              confirmButtonRef.current?.focus();
            }
          }
          break;

        default:
          break;
      }
    }, 100);
  };

  // Function to center selection on open
  const centerDateSelection = () => {
    if (isOpen) {
      const yearsArr = isNextYears ? nextYears : prevYears;

      const verifyIndex = (index, length) => Math.max(0, Math.min(index, length - 1));

      const dayIndex = verifyIndex(selectedDay - 1, days.length);
      const monthIndex = verifyIndex(selectedMonth, months.length);
      const yearIndex = verifyIndex(
        yearsArr.findIndex((year) => year === selectedYear),
        yearsArr.length
      );

      setFocusDIndex(dayIndex);
      setFocusMIndex(monthIndex);
      setFocusYIndex(yearIndex);

      requestAnimationFrame(() => {
        dateRefs.current[dayIndex]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        monthRefs.current[monthIndex]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        yearRefs.current[yearIndex]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  };

  useEffect(() => {
    centerDateSelection();
  }, [isFocusOn, isOpen]);

  //  Hooks
  useEffect(() => {
    // Function to handle click events
    const handleClickOutside = (event) => {
      if (dropMenu.current && !dropMenu.current.contains(event.target)) {
        // console.log('Clicked outside the div');
        // console.log('isOpen', isOpen);
        if (isOpen) toggleOpen();
      } else {
        // console.log('Clicked inside the div');
        // Do nothing
      }
    };

    // Add event listener to the document
    document.addEventListener('click', handleClickOutside);

    // Cleanup event listener on component unmount
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (disabled) {
      setIsOpen(false);
      setIsFocus(false);
    }
  }, [disabled]);

  useEffect(() => {
    if (isBlur) {
      onBlur({
        type: 'blur',
        target: {
          id,
          name: id,
          value: isBlur
        }
      });
    }
  }, [isBlur]);

  useEffect(() => {
    if (valueProp) {
      const [day, month, year] = valueProp.split('.');
      const formattedDate = new Date(`${year}-${month}-${day}`);
      setValue(formattedDate);
      setSelectedDay(Number(day));
      setSelectedMonth(month-1);
      setSelectedYear(Number(year));
    }
  }, [])

  return (
    <div className="dropdown" ref={dropMenu}>
      <div className="mb-1">
        <label htmlFor={id} className="form-label" id={`${id}-label`}>
          {tA11y(label).isHtml ? (
            <span dangerouslySetInnerHTML={{ __html: sanitizeRichText(tA11y(label).content) }} />
          ) : (
            <span>{tA11y(label).content}</span>
          )}
          {isOptional && (
            <span className="ek-labeltext-12-medium text-ek-blue-placeholder ps-4">(optional)</span>
          )}
        </label>
        <div
          ref={datePickerRef}
          role="button"
          tabIndex={0}
          className={`
            input-group input-group-datepicker
            ${valid ? 'valid' : ''}
            ${
              invalid && (!isOpen ? !isOpen : isTouched)
                ? 'invalid animate__animated animate__shakeX'
                : ''
            }
            ${isFocus ? 'focus' : ''}
            ${isBlur ? 'blur' : ''}
            ${isTouched ? 'touched' : ''}
            ${disabled ? 'disabled' : ''}
            ${customClass}
          `}
          onClick={toggleOpen}
          onKeyDown={handleKeyDown}
          name={restProps.name}
          aria-describedby={restProps['aria-describedby']}>
          <div
            className={`form-control form-control-placeholder d-flex align-items-center justify-content-start ek-placeholder-text text-ek-blue-placeholder'
            `}>
            {placeholderText()}
          </div>
          <span className="input-group-text ps-0 px-6 bg-transparent">
            <Icons name='DATE' alt="Dropdown input" colorType='dark' />
          </span>
        </div>
      </div>

      {isOpen && !popUpPicker && (
        <div className={`${transparentDropDown ? '' : ' bg-ek-white'} p-16 rounded-4 `}>
          <div className="input-group-datepicker-list date-picker">
            <div className="row date-picker-row h-100">
              <div className="col-4 text-center date-picker-day">
                <div className="p-1">
                  {days.map((day, index) => (
                    <button
                      key={day}
                      tabIndex={-1}
                      ref={(el) => (dateRefs.current[index] = el)}
                      className={`dropdown-item py-1 ${
                        day === selectedDay && 'bg-secondary rounded-pill ek-copy-16-semibold'
                      } ${focusDIndex === index && isFocusOn === 'D' ? 'focus-cell' : ''}`}
                      type="button"
                      onClick={() => handleDayChange(day)}>
                      {day}
                    </button>
                  ))}
                </div>
              </div>
              <div className="col-4 text-center date-picker-month">
                <div className="p-1">
                  {months.map((month, index) => (
                    <button
                      key={month}
                      tabIndex={-1}
                      ref={(el) => (monthRefs.current[index] = el)}
                      className={`dropdown-item py-1 ${
                        index === selectedMonth && 'bg-secondary rounded-pill ek-copy-16-semibold'
                      } ${focusMIndex === index && isFocusOn === 'M' ? 'focus-cell' : ''}`}
                      type="button"
                      onClick={() => handleMonthChange(index)}>
                      {month}
                    </button>
                  ))}
                </div>
              </div>
              <div className="col-4 text-center date-picker-year">
                <div className="p-1">
                  {isNextYears
                    ? nextYears.map((year, index) => (
                        <button
                          key={year}
                          tabIndex={-1}
                          ref={(el) => (yearRefs.current[index] = el)}
                          className={`dropdown-item py-1 ${
                            year === selectedYear && 'bg-secondary rounded-pill ek-copy-16-semibold'
                          } ${focusYIndex === index && isFocusOn === 'Y' ? 'focus-cell' : ''}`}
                          type="button"
                          onClick={() => handleYearChange(year)}>
                          {year}
                        </button>
                      ))
                    : prevYears.map((year, index) => (
                        <button
                          key={year}
                          tabIndex={-1}
                          ref={(el) => (yearRefs.current[index] = el)}
                          className={`dropdown-item py-1 ${
                            year === selectedYear && 'bg-secondary rounded-pill ek-copy-16-semibold'
                          } ${focusYIndex === index && isFocusOn === 'Y' ? 'focus-cell' : ''}`}
                          type="button"
                          onClick={() => handleYearChange(year)}>
                          {year}
                        </button>
                      ))}
                </div>
              </div>
              <div className="col-12 d-flex align-items-center justify-content-center">
                <ButtonPrimary
                  tabIndex={0}
                  onClick={onSelectDate}
                  onKeyDown={onSelectDate}
                  type="button"
                  label="Bestätigen"
                  btnRef={confirmButtonRef}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {isOpen && popUpPicker && (
        <div className="popup-date-picker d-flex justify-content-center align-items-start p-4">
          <div className="bg-white p-4 rounded-4 position-relative">
            <div className="input-group-datepicker-list date-picker">
              <div className="row date-picker-row h-100">
                <div className="col-4 text-center date-picker-day">
                  <div className="p-1">
                    {days.map((day, index) => (
                      <button
                        key={day}
                        tabIndex={-1}
                        ref={(el) => (dateRefs.current[index] = el)}
                        className={`dropdown-item py-1 ${
                          day === selectedDay && 'bg-secondary rounded-pill ek-copy-16-semibold'
                        } ${focusDIndex === index && isFocusOn === 'D' ? 'focus-cell' : ''}`}
                        type="button"
                        onClick={() => handleDayChange(day)}>
                        {day}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="col-4 text-center date-picker-month">
                  <div className="p-1">
                    {months.map((month, index) => (
                      <button
                        key={month}
                        tabIndex={-1}
                        ref={(el) => (monthRefs.current[index] = el)}
                        className={`dropdown-item py-1 ${
                          index === selectedMonth && 'bg-secondary rounded-pill ek-copy-16-semibold'
                        } ${focusMIndex === index && isFocusOn === 'M' ? 'focus-cell' : ''}`}
                        type="button"
                        onClick={() => handleMonthChange(index)}>
                        {month}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="col-4 text-center date-picker-year">
                  <div className="p-1">
                    {isNextYears
                      ? nextYears.map((year, index) => (
                          <button
                            key={year}
                            tabIndex={-1}
                            ref={(el) => (yearRefs.current[index] = el)}
                            className={`dropdown-item py-1 ${
                              year === selectedYear &&
                              'bg-secondary rounded-pill ek-copy-16-semibold'
                            } ${focusYIndex === index && isFocusOn === 'Y' ? 'focus-cell' : ''}`}
                            type="button"
                            onClick={() => handleYearChange(year)}>
                            {year}
                          </button>
                        ))
                      : prevYears.map((year, index) => (
                          <button
                            key={year}
                            tabIndex={-1}
                            ref={(el) => (yearRefs.current[index] = el)}
                            className={`dropdown-item py-1 ${
                              year === selectedYear &&
                              'bg-secondary rounded-pill ek-copy-16-semibold'
                            } ${focusYIndex === index && isFocusOn === 'Y' ? 'focus-cell' : ''}`}
                            type="button"
                            onClick={() => handleYearChange(year)}>
                            {year}
                          </button>
                        ))}
                  </div>
                </div>
                <div className="col-12 d-flex align-items-center justify-content-center">
                  <ButtonPrimary
                    tabIndex={0}
                    onClick={onSelectDate}
                    onKeyDown={onSelectDate}
                    type="button"
                    label="Bestätigen"
                    btnRef={confirmButtonRef}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {hint && !valid && !invalid && (
        <div id={`${id}-hint`} className="form-text pt-4 ps-4" aria-live="polite">
          <Notification message={hint} />
        </div>
      )}
      {invalid && invalidMessage && (
        <div id={`${id}-error`} className="form-text pt-4 ps-4" aria-live="polite">
          <div id={restProps['aria-describedby']}>
            <Notification type={appAlert.ERROR} message={invalidMessage} />
          </div>
        </div>
      )}
      {valid && validMessage && (
        <div id={`${id}-success`} className="form-text pt-4 ps-4" aria-live="polite">
          <Notification type={appAlert.SUCCESS} message={validMessage} />
        </div>
      )}
    </div>
  );
}

FormDatePicker.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  hint: PropTypes.string,
  value: PropTypes.string,
  invalid: PropTypes.bool,
  invalidMessage: PropTypes.string,
  valid: PropTypes.bool,
  validMessage: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  isNextYears: PropTypes.bool,
  popUpPicker: PropTypes.bool,
  transparentDropDown: PropTypes.bool,
  customClass: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  isOptional: PropTypes.bool
};

export default FormDatePicker;
